import React, { useCallback, useEffect, useState } from "react";
import { Box, Button } from "@material-ui/core";
import { Redirect } from "react-router-dom";
import { useGlStatus } from "../../components/gl-shared/gl-status";
import * as glApiDefault from "../../components/gl-shared/gl-api";
import "./gl-deck-page.scss";

const TERMINAL_COPY = {
  unavailable: "Couldn't load your due cards. Try again.",
  disabled: "The review deck isn't available right now.",
  cap: "You've reached today's limit."
};
const OUTCOMES = [
  { key: "correct", label: "Got it" },
  { key: "incorrect", label: "Missed it" },
  { key: "skipped", label: "Skip" }
];

/**
 * GL-5b due-queue page (GL-1 §4 verbatim). DARK: with the flag/audience off the
 * route renders NO surface (redirect). Consumes GET /gl/deck/due and POSTs
 * /gl/deck/recall; per-card front → reveal → back → correct/incorrect/skipped.
 * Counts are shown honestly from the DTO; empty + terminal states are explicit;
 * no silent spinner. Card text is in-memory only (P12); no client events.
 */
const GlDeckPage = ({ glApi = glApiDefault }) => {
  // B2 mount honesty: build flags AND the server's confirmed {allowed:true}.
  // While the probe is in flight nothing loads; a SETTLED denial redirects.
  const { allowed: glAllowed, settled: glSettled } = useGlStatus();
  const [queue, setQueue] = useState([]);
  const [counts, setCounts] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recallError, setRecallError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await glApi.getDue();
    setLoading(false);
    if (res.ok) {
      setQueue(((res.data && res.data.due) || []).slice());
      setCounts((res.data && res.data.counts) || null);
      setRevealed(false);
    } else {
      setError(res.glError || "unavailable");
    }
  }, [glApi]);

  useEffect(() => {
    if (glAllowed) {
      load();
    }
  }, [glAllowed, load]);

  if (glSettled && !glAllowed) {
    return <Redirect to="/create-quiz" />;
  }

  const current = queue[0];

  const onRecall = async (outcome) => {
    if (!current || busy) {
      return;
    }
    setBusy(true);
    setRecallError(null);
    const res = await glApi.recall(current.cardId, outcome);
    setBusy(false);
    if (!res.ok) {
      setRecallError(res.glError || "unavailable");
      return;
    }
    // Contract row 8: recall returns the BARE rescheduled card — no counts.
    // Derive today's count locally (a non-skip removes one card from today's
    // queue); the authoritative counts refresh on the next /gl/deck/due load.
    if (outcome !== "skipped") {
      setCounts((prev) =>
        prev ? { ...prev, dueToday: Math.max(0, (prev.dueToday || 0) - 1) } : prev
      );
    }
    setQueue((prev) => {
      const [head, ...rest] = prev;
      // §4: skipped leaves the card DUE (send to the back of this session's
      // queue); correct/incorrect reschedule it out of today's queue.
      const next = outcome === "skipped" ? [...rest, head] : rest;
      return next;
    });
    setRevealed(false);
    setStatus(
      outcome === "skipped" ? "Skipped — kept for later." : "Recorded. Next card."
    );
  };

  return (
    <Box className="gl-deck-page">
      <Box className="gl-deck-header">
        <h1 className="gl-deck-title">Due for review</h1>
        {counts && (
          <Box className="gl-deck-counts" aria-live="polite">
            {counts.dueToday} due today · {counts.total} total
          </Box>
        )}
      </Box>

      <Box className="gl-deck-status" role="status" aria-live="polite">{status}</Box>

      {loading && <Box className="gl-deck-loading">Loading…</Box>}

      {!loading && error && (
        <Box className="gl-deck-terminal" role="alert">
          {TERMINAL_COPY[error] || TERMINAL_COPY.unavailable}
          <Button className="gl-deck-retry" variant="contained" color="primary" onClick={load}>
            Try again
          </Button>
        </Box>
      )}

      {!loading && !error && !current && (
        <Box className="gl-deck-empty">
          <p>You're all caught up — no cards due right now.</p>
        </Box>
      )}

      {!loading && !error && current && (
        <Box className="gl-deck-card" component="section" aria-label="Review card">
          <Box className="gl-deck-face gl-deck-front">{current.front}</Box>

          {!revealed ? (
            <Button
              className="gl-deck-reveal"
              variant="contained"
              color="primary"
              onClick={() => setRevealed(true)}
            >
              Reveal answer
            </Button>
          ) : (
            <>
              <Box className="gl-deck-face gl-deck-back">{current.back}</Box>
              {recallError && (
                <Box className="gl-deck-row-error" role="alert">
                  {TERMINAL_COPY[recallError] || TERMINAL_COPY.unavailable}
                </Box>
              )}
              <Box className="gl-deck-outcomes">
                {OUTCOMES.map((o) => (
                  <Button
                    key={o.key}
                    className={`gl-deck-outcome gl-deck-outcome--${o.key}`}
                    variant={o.key === "skipped" ? "text" : "outlined"}
                    disabled={busy}
                    onClick={() => onRecall(o.key)}
                  >
                    {o.label}
                  </Button>
                ))}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default GlDeckPage;
